import { replyWithMenuHeaderText } from './attachButtonMenuItemBuilder.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47713 */
import extractAttachmentSuggestions from './extractAttachmentSuggestions';
import { CloudIconWithOverlay, getIconCSSForAttachment } from 'owa-file-icon';
import { FilterableMenuItem } from 'owa-filterable-menu';
import { ComposeViewState } from 'owa-mail-compose-store';

import * as React from 'react';
import {
    AttachmentSuggestions,
    ReplyWithAttachmentSuggestion,
    ReplyWithEndpointType,
} from 'owa-mail-store';
import {
    IContextualMenuItem,
    ContextualMenuItemType,
} from 'office-ui-fabric-react/lib/ContextualMenu';
import {
    fixFileName,
    getAttachmentSuggestionMetadata,
    getTooltipForAttachmentSuggestion,
    onAttachmentClicked,
    middleTruncateFileName,
    getFileExtension,
} from './attachButtonMenuItemBuilderUtils';

const styles = require('../components/AttachButtonMenuItem.scss');

/**
 * Used for Attach button in Compose bottom bar.
 */
export function buildReplyWithContextualMenuItems(
    composeViewState: ComposeViewState,
    replyWithSuggestions: AttachmentSuggestions,
    mruSuggestions: ReplyWithAttachmentSuggestion[]
): IContextualMenuItem[] {
    let attachmentSuggestionMenuItems: IContextualMenuItem[] = [];

    const suggestions: ReplyWithAttachmentSuggestion[] = extractAttachmentSuggestions(
        replyWithSuggestions,
        mruSuggestions
    );

    if (suggestions.length > 0) {
        let keyIndex: number = 0;
        attachmentSuggestionMenuItems = suggestions.map(suggestion =>
            createAttachButtonContextualMenuItem(suggestion, keyIndex++, composeViewState)
        );

        return [createReplyWithContextualMenuItemHeader()]
            .concat(attachmentSuggestionMenuItems)
            .concat([createReplyWithContextualMenuItemSeparator()]);
    } else {
        return attachmentSuggestionMenuItems;
    }
}

/**
 * Used for Attach button in Compose commanding bar.
 */
export function buildReplyWithFilterableMenuItems(
    composeViewState: ComposeViewState,
    replyWithSuggestions: AttachmentSuggestions,
    mruSuggestions: ReplyWithAttachmentSuggestion[]
): FilterableMenuItem[] {
    let attachmentSuggestionMenuItems: FilterableMenuItem[] = [];

    const suggestions: ReplyWithAttachmentSuggestion[] = extractAttachmentSuggestions(
        replyWithSuggestions,
        mruSuggestions
    );

    if (suggestions.length > 0) {
        let keyIndex: number = 0;
        attachmentSuggestionMenuItems = suggestions.map(suggestion =>
            createAttachButtonFilterableMenuItem(suggestion, keyIndex++, composeViewState)
        );

        return [createReplyWithFilterableMenuItemHeader()].concat(attachmentSuggestionMenuItems);
    } else {
        return attachmentSuggestionMenuItems;
    }
}

export function createAttachButtonContextualMenuItem(
    suggestion: ReplyWithAttachmentSuggestion,
    keyIndex: number,
    composeViewState: ComposeViewState
): IContextualMenuItem {
    return {
        key: 'suggestion' + keyIndex,
        className: styles.attachmentSuggestionButtonRoot,
        onRender: (
            item: any,
            dismissMenu: (ev?: any, dismissAll?: boolean) => void
        ): React.ReactNode => {
            return createAttachmentSuggestionButton(
                suggestion,
                keyIndex,
                composeViewState,
                dismissMenu
            );
        },
    };
}

function createAttachButtonFilterableMenuItem(
    suggestion: ReplyWithAttachmentSuggestion,
    keyIndex: number,
    composeViewState: ComposeViewState
): FilterableMenuItem {
    return new FilterableMenuItem(
        null /* name */,
        null /* icon */,
        null /* onClick */,
        null /* shouldShow */,
        null /* displayOption */,
        null /* disabled */,
        null /* menuContent */,
        keyIndex.toString(),
        (item: any, dismissMenu: (ev?: any, dismissAll?: boolean) => void): React.ReactNode => {
            return createAttachmentSuggestionButton(
                suggestion,
                keyIndex,
                composeViewState,
                dismissMenu
            );
        },
        null /* checked */,
        null /* ariaLabel */,
        null /* title */,
        styles.attachmentSuggestionButtonRoot
    );
}

function createAttachmentSuggestionButton(
    suggestion: ReplyWithAttachmentSuggestion,
    keyIndex: number,
    composeViewState: ComposeViewState,
    dismissMenu: (ev?: any, dismissAll?: boolean) => void
): JSX.Element {
    return (
        <button
            className={styles.attachmentSuggestionButton}
            title={getTooltipForAttachmentSuggestion(suggestion)}
            name={suggestion.FileName}
            aria-label={suggestion.FileName}
            aria-posinset={1}
            aria-setsize={7}
            aria-disabled="false"
            role="menuitem"
            onClick={() => {
                onAttachmentClicked(suggestion, keyIndex, composeViewState);
                dismissMenu();
            }}
            tabIndex={0}>
            <div className={styles.attachmentSuggestionButtonContent}>
                <div
                    className={styles.attachmentSuggestionButtonImageContainer}
                    role="presentation"
                    aria-hidden="true">
                    {getIconForSuggestedAttachment(suggestion)}
                </div>
                <div>
                    <div className={styles.attachmentSuggestionButtonTextContainer}>
                        <div className={styles.attachmentSuggestionPrimaryText}>
                            {middleTruncateFileName(
                                fixFileName(suggestion) + getFileExtension(suggestion)
                            )}
                        </div>
                    </div>
                    <div className={styles.attachmentSuggestionSecondaryText}>
                        {getAttachmentSuggestionMetadata(suggestion)}
                    </div>
                </div>
            </div>
        </button>
    );
}

function createReplyWithContextualMenuItemHeader(): IContextualMenuItem {
    return {
        key: 'replyWithHeader',
        name: loc(replyWithMenuHeaderText),
        itemType: ContextualMenuItemType.Header,
        iconProps: {
            iconName: null,
            styles: {
                root: {
                    margin: '0px',
                    width: '0px',
                },
            },
        },
        className: styles.replyWithHeaderForBottomBar,
    };
}

function createReplyWithFilterableMenuItemHeader(): FilterableMenuItem {
    return new FilterableMenuItem(
        null /* name */,
        null /* icon */,
        null /* onClick */,
        null /* shouldShow */,
        null /* displayOption */,
        null /* disabled */,
        null /* menuContent */,
        'replyWithHeader',
        () => {
            return (
                <div className={styles.replyWithHeaderForCommandingBar}>
                    <span>{loc(replyWithMenuHeaderText)}</span>
                </div>
            );
        }
    );
}

function createReplyWithContextualMenuItemSeparator(): IContextualMenuItem {
    return {
        key: 'replyWithSeparator',
        iconProps: {
            iconName: null,
            styles: {
                root: {
                    margin: '0px',
                    width: '0px',
                },
            },
        },
        className: styles.replyWithSeparator,
        itemType: ContextualMenuItemType.Divider,
    };
}

function getIconForSuggestedAttachment(suggestion: ReplyWithAttachmentSuggestion): JSX.Element {
    const nameWithExtension = suggestion.FileName + getFileExtension(suggestion);

    if (suggestion.Endpoint === ReplyWithEndpointType.MRUClassic) {
        // Classic attachment, so use regular icon
        const attachmentIconCSS: string = getIconCSSForAttachment(
            nameWithExtension,
            null /* contentType */,
            false /* isBlocked */,
            false /* isFolder */,
            null /* embeddedItemClass */
        ).medium;

        return <div className={attachmentIconCSS} />;
    } else {
        // Cloudy attachment, so use cloudy icon
        return (
            <CloudIconWithOverlay
                fileName={nameWithExtension}
                contentType={null}
                isBlocked={false}
                isFolder={false}
                embeddedItemClass={null}
                cloudyIconStyle={styles.cloudyIcon}
                fileIconStyle={styles.fileIcon}
            />
        );
    }
}
